import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import chatService from "services/chatService";

export const initialState = {
  // Conversations
  conversations: [],
  conversationsLoading: false,
  conversationsError: null,

  // Active conversation
  activeConversationId: null,
  activeConversation: null,

  // Messages for active conversation
  messages: [],
  messagesLoading: false,
  messagesError: null,
  hasMoreMessages: true,

  // Typing indicators (conversationId -> [users])
  typingIndicators: {},

  // Unread counts
  unreadCount: 0,
  unreadByConversation: {},

  // Online users
  onlineUsers: [],

  // Chat modal state
  chatModalOpen: false,
  newChatModalOpen: false,

  // Sending state
  sending: false,
};

// Async thunks
export const fetchConversations = createAsyncThunk(
  "chat/fetchConversations",
  async (_, { rejectWithValue }) => {
    try {
      const response = await chatService.getConversations();
      // apiHandle returns res.data, so response = { success, message, data: { conversations: [...] } }
      return response.data?.conversations || [];
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch conversations");
    }
  }
);

export const fetchMessages = createAsyncThunk(
  "chat/fetchMessages",
  async ({ conversationId, before = null, limit = 50 }, { rejectWithValue }) => {
    try {
      const response = await chatService.getMessages(conversationId, limit, before);
      // apiHandle returns res.data, so response = { success, message, data: { messages: [...], pagination: {...} } }
      return {
        conversationId,
        messages: response.data?.messages || [],
        pagination: response.data?.pagination || {},
        append: !!before,
      };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch messages");
    }
  }
);

export const startDirectConversation = createAsyncThunk(
  "chat/startDirectConversation",
  async ({ userId, recipientData }, { rejectWithValue }) => {
    try {
      const response = await chatService.startDirectConversation(userId);
      // apiHandle returns res.data, so response = { success, message, data: { conversation: {...} } }
      const conversation = response.data?.conversation || response.conversation || response;
      
      // If API doesn't return populated participants, add the recipient data
      if (conversation && recipientData && conversation.participants) {
        const hasPopulatedRecipient = conversation.participants.some(
          (p) => p._id === recipientData._id && p.firstName
        );
        if (!hasPopulatedRecipient) {
          const participantIndex = conversation.participants.findIndex(
            (p) => p._id === recipientData._id || p === recipientData._id
          );
          if (participantIndex !== -1) {
            conversation.participants[participantIndex] = recipientData;
          } else {
            // Participant might be a string ID, replace it
            conversation.participants = conversation.participants.map((p) => 
              (typeof p === 'string' && p === recipientData._id) ? recipientData : p
            );
          }
        }
      }
      
      return conversation;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to start conversation");
    }
  }
);

export const createGroupConversation = createAsyncThunk(
  "chat/createGroupConversation",
  async ({ name, participantIds, groupAvatar }, { rejectWithValue }) => {
    try {
      const response = await chatService.createGroupConversation(participantIds, name, groupAvatar);
      // apiHandle returns res.data, so response = { success, message, data: { conversation: {...} } }
      return response.data?.conversation || response.conversation || response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to create group");
    }
  }
);

export const fetchUnreadCount = createAsyncThunk(
  "chat/fetchUnreadCount",
  async (_, { rejectWithValue }) => {
    try {
      const response = await chatService.getUnreadCount();
      return response.data?.totalUnread || 0;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch unread count");
    }
  }
);

export const sendMessage = createAsyncThunk(
  "chat/sendMessage",
  async ({ conversationId, content, messageType = "text", attachments, replyTo }, { rejectWithValue }) => {
    try {
      const response = await chatService.sendMessage(conversationId, { 
        content, 
        messageType, 
        attachments, 
        replyTo 
      });
      // apiHandle returns res.data, so response = { success, message, data: { message: {...} } }
      return response.data?.message || response.message || response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to send message");
    }
  }
);

export const markMessagesAsRead = createAsyncThunk(
  "chat/markMessagesAsRead",
  async ({ conversationId, messageIds = [] }, { rejectWithValue }) => {
    try {
      await chatService.markAsRead(conversationId, messageIds);
      return { conversationId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to mark as read");
    }
  }
);

export const deleteMessage = createAsyncThunk(
  "chat/deleteMessage",
  async ({ messageId, forEveryone = false }, { rejectWithValue }) => {
    try {
      await chatService.deleteMessage(messageId, forEveryone);
      return { messageId, forEveryone };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to delete message");
    }
  }
);

export const archiveConversation = createAsyncThunk(
  "chat/archiveConversation",
  async (conversationId, { rejectWithValue }) => {
    try {
      await chatService.archiveConversation(conversationId);
      return conversationId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to archive conversation");
    }
  }
);

export const deleteConversation = createAsyncThunk(
  "chat/deleteConversation",
  async (conversationId, { rejectWithValue }) => {
    try {
      await chatService.deleteConversation(conversationId);
      return conversationId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete conversation");
    }
  }
);

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    // Set active conversation
    setActiveConversation: (state, action) => {
      state.activeConversationId = action.payload;
      state.activeConversation = state.conversations.find(
        (c) => c._id === action.payload
      ) || null;
      state.messages = [];
      state.hasMoreMessages = true;
    },

    // Clear active conversation
    clearActiveConversation: (state) => {
      state.activeConversationId = null;
      state.activeConversation = null;
      state.messages = [];
      state.hasMoreMessages = true;
    },

    // Add new message (from socket)
    addMessage: (state, action) => {
      const { message, conversationId } = action.payload;
      
      // Match by full ID or partial ID (last 8 chars)
      const fullConvId = conversationId;
      const activeId = state.activeConversationId;
      const isActiveConversation = activeId && (
        fullConvId === activeId || 
        fullConvId?.endsWith(activeId) || 
        activeId?.endsWith(fullConvId?.slice(-8))
      );
      
      // Add to messages if it's the active conversation
      if (isActiveConversation) {
        // Check if message already exists by ID
        const existingIndex = state.messages.findIndex((m) => m._id === message._id);
        if (existingIndex !== -1) {
          // Update existing message (might be from optimistic update)
          state.messages[existingIndex] = { ...state.messages[existingIndex], ...message, status: 'sent' };
        } else {
          // Check for optimistic message with 'sending' status that matches content
          const optimisticIndex = state.messages.findIndex((m) => 
            m.status === 'sending' && 
            m.content === message.content &&
            m.sender?._id === message.sender?._id
          );
          if (optimisticIndex !== -1) {
            // Replace optimistic message with real one
            state.messages[optimisticIndex] = { ...message, status: 'sent' };
          } else {
            // New message from another user, add it
            state.messages.push(message);
          }
        }
      }

      // Update conversation's lastMessage - match by full or partial ID
      const convIndex = state.conversations.findIndex((c) => 
        c._id === fullConvId || 
        c._id?.endsWith(fullConvId?.slice(-8)) ||
        fullConvId?.endsWith(c._id?.slice(-8))
      );
      if (convIndex !== -1) {
        state.conversations[convIndex].lastMessage = message;
        
        // Move to top
        if (convIndex > 0) {
          const [conv] = state.conversations.splice(convIndex, 1);
          state.conversations.unshift(conv);
        }
      }
    },

    // Add optimistic message
    addOptimisticMessage: (state, action) => {
      state.messages.push(action.payload);
      state.sending = true;
    },

    // Update optimistic message status
    updateMessageStatus: (state, action) => {
      const { tempId, message, status } = action.payload;
      const index = state.messages.findIndex((m) => m._id === tempId);
      if (index !== -1) {
        if (message) {
          state.messages[index] = { ...message, status: 'sent' };
        } else {
          state.messages[index].status = status;
        }
      }
      state.sending = false;
    },

    // Add new conversation (from socket)
    addConversation: (state, action) => {
      const exists = state.conversations.some((c) => c._id === action.payload._id);
      if (!exists) {
        state.conversations.unshift(action.payload);
      }
    },

    // Update typing indicator
    setTypingIndicator: (state, action) => {
      const { conversationId, user, isTyping } = action.payload;
      
      // Use full conversation ID for storage, but match flexibly
      const activeId = state.activeConversationId;
      const isActiveConv = activeId && (
        conversationId === activeId || 
        conversationId?.endsWith(activeId) || 
        activeId?.endsWith(conversationId?.slice(-8)) ||
        conversationId?.endsWith(activeId?.slice(-8))
      );
      
      // Store using the key that matches our active conversation for easier lookup
      const storeKey = isActiveConv ? activeId : conversationId;
      
      if (!state.typingIndicators[storeKey]) {
        state.typingIndicators[storeKey] = [];
      }

      if (isTyping) {
        const exists = state.typingIndicators[storeKey].some((u) => u._id === user._id);
        if (!exists) {
          state.typingIndicators[storeKey].push(user);
        }
      } else {
        state.typingIndicators[storeKey] = state.typingIndicators[storeKey].filter(
          (u) => u._id !== user._id
        );
      }
    },

    // Update user online status
    setUserOnline: (state, action) => {
      const userId = action.payload;
      if (!state.onlineUsers.includes(userId)) {
        state.onlineUsers.push(userId);
      }

      // Update in conversations' participants
      state.conversations.forEach((conv) => {
        conv.participants?.forEach((p) => {
          if (p._id === userId) {
            p.isOnline = true;
          }
        });
      });
    },

    setUserOffline: (state, action) => {
      const userId = action.payload;
      state.onlineUsers = state.onlineUsers.filter((id) => id !== userId);

      // Update in conversations' participants
      state.conversations.forEach((conv) => {
        conv.participants?.forEach((p) => {
          if (p._id === userId) {
            p.isOnline = false;
            p.lastSeen = new Date().toISOString();
          }
        });
      });
    },

    // Update unread count
    incrementUnreadCount: (state, action) => {
      const { conversationId } = action.payload;
      state.unreadCount += 1;
      
      // Find conversation with flexible ID matching
      const conv = state.conversations.find((c) => 
        c._id === conversationId || 
        c._id?.endsWith(conversationId?.slice(-8)) ||
        conversationId?.endsWith(c._id?.slice(-8))
      );
      
      // Use the conversation's actual ID for storing unread count
      const storeId = conv?._id || conversationId;
      state.unreadByConversation[storeId] = 
        (state.unreadByConversation[storeId] || 0) + 1;
      
      if (conv) {
        conv.unreadCount = (conv.unreadCount || 0) + 1;
      }
    },

    clearConversationUnread: (state, action) => {
      const conversationId = action.payload;
      
      // Find conversation with flexible ID matching
      const conv = state.conversations.find((c) => 
        c._id === conversationId || 
        c._id?.endsWith(conversationId?.slice(-8)) ||
        conversationId?.endsWith(c._id?.slice(-8))
      );
      
      const storeId = conv?._id || conversationId;
      const count = state.unreadByConversation[storeId] || 0;
      state.unreadCount = Math.max(0, state.unreadCount - count);
      state.unreadByConversation[storeId] = 0;
      
      if (conv) {
        conv.unreadCount = 0;
      }
    },

    // Mark messages as read locally
    markMessagesReadLocally: (state, action) => {
      const { conversationId, readBy } = action.payload;
      
      const activeId = state.activeConversationId;
      const isActiveConversation = activeId && (
        conversationId === activeId || 
        conversationId?.endsWith(activeId) || 
        activeId?.endsWith(conversationId?.slice(-8))
      );
      
      if (isActiveConversation) {
        state.messages.forEach((msg) => {
          if (!msg.readBy) msg.readBy = [];
          if (!msg.readBy.some((r) => r.userId === readBy._id)) {
            msg.readBy.push({ userId: readBy._id, readAt: new Date().toISOString() });
          }
        });
      }
    },

    // Remove message locally
    removeMessage: (state, action) => {
      const { messageId, forEveryone } = action.payload;
      if (forEveryone) {
        const msg = state.messages.find((m) => m._id === messageId);
        if (msg) {
          msg.isDeleted = true;
          msg.content = "This message was deleted";
        }
      } else {
        state.messages = state.messages.filter((m) => m._id !== messageId);
      }
    },

    // Modal controls
    openChatModal: (state) => {
      state.chatModalOpen = true;
    },

    closeChatModal: (state) => {
      state.chatModalOpen = false;
      state.activeConversationId = null;
      state.activeConversation = null;
      state.messages = [];
    },

    openNewChatModal: (state) => {
      state.newChatModalOpen = true;
    },

    closeNewChatModal: (state) => {
      state.newChatModalOpen = false;
    },

    // Reset chat state (on logout)
    resetChatState: () => initialState,

    // Clear errors
    clearConversationsError: (state) => {
      state.conversationsError = null;
    },

    clearMessagesError: (state) => {
      state.messagesError = null;
    },
  },

  extraReducers: (builder) => {
    // Fetch conversations
    builder
      .addCase(fetchConversations.pending, (state) => {
        state.conversationsLoading = true;
        state.conversationsError = null;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.conversationsLoading = false;
        state.conversations = action.payload;
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.conversationsLoading = false;
        state.conversationsError = action.payload;
      });

    // Fetch messages
    builder
      .addCase(fetchMessages.pending, (state) => {
        state.messagesLoading = true;
        state.messagesError = null;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.messagesLoading = false;
        const { conversationId, messages, pagination, append } = action.payload;
        
        // Set activeConversationId from the full conversation ID
        if (!append && conversationId) {
          // Find the full conversation to get the complete ID
          const conversation = state.conversations.find((c) => 
            c._id === conversationId || 
            c._id?.endsWith(conversationId) ||
            conversationId?.endsWith(c._id?.slice(-8))
          );
          if (conversation) {
            state.activeConversationId = conversation._id;
            state.activeConversation = conversation;
          } else {
            // If conversation not found yet, use the provided ID
            state.activeConversationId = conversationId;
          }
        }
        
        if (append) {
          state.messages = [...messages, ...state.messages];
        } else {
          state.messages = messages;
        }
        
        state.hasMoreMessages = pagination?.hasMore || false;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.messagesLoading = false;
        state.messagesError = action.payload;
      });

    // Start direct conversation
    builder
      .addCase(startDirectConversation.fulfilled, (state, action) => {
        const exists = state.conversations.some((c) => c._id === action.payload._id);
        if (!exists) {
          state.conversations.unshift(action.payload);
        }
        state.activeConversationId = action.payload._id;
        state.activeConversation = action.payload;
        state.newChatModalOpen = false;
      });

    // Create group conversation
    builder
      .addCase(createGroupConversation.fulfilled, (state, action) => {
        state.conversations.unshift(action.payload);
        state.activeConversationId = action.payload._id;
        state.activeConversation = action.payload;
        state.newChatModalOpen = false;
      });

    // Fetch unread count
    builder
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload;
      });

    // Send message
    builder
      .addCase(sendMessage.pending, (state) => {
        state.sending = true;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.sending = false;
        const message = action.payload;
        if (message && message._id) {
          // Find and update the optimistic message (with 'sending' status)
          const tempIndex = state.messages.findIndex((m) => m.status === 'sending');
          if (tempIndex !== -1) {
            // Replace optimistic message with real message
            state.messages[tempIndex] = { ...message, status: 'sent' };
          } else {
            // Check if message already exists (might have been added via socket)
            const existingIndex = state.messages.findIndex((m) => m._id === message._id);
            if (existingIndex !== -1) {
              // Update existing message status
              state.messages[existingIndex] = { ...state.messages[existingIndex], ...message, status: 'sent' };
            }
            // Don't add new message here - socket will handle it
          }
          
          // Update conversation's lastMessage
          const convIndex = state.conversations.findIndex((c) => 
            c._id === message.conversationId || 
            c._id === state.activeConversationId ||
            c._id?.endsWith(message.conversationId?.slice(-8))
          );
          if (convIndex !== -1) {
            state.conversations[convIndex].lastMessage = message;
          }
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.sending = false;
        // Mark optimistic message as failed
        const tempIndex = state.messages.findIndex((m) => m.status === 'sending');
        if (tempIndex !== -1) {
          state.messages[tempIndex].status = 'failed';
        }
      });

    // Mark as read
    builder
      .addCase(markMessagesAsRead.fulfilled, (state, action) => {
        const { conversationId } = action.payload;
        const count = state.unreadByConversation[conversationId] || 0;
        state.unreadCount = Math.max(0, state.unreadCount - count);
        state.unreadByConversation[conversationId] = 0;
        
        const conv = state.conversations.find((c) => c._id === conversationId);
        if (conv) {
          conv.unreadCount = 0;
        }
      });

    // Delete message
    builder
      .addCase(deleteMessage.fulfilled, (state, action) => {
        const { messageId, forEveryone } = action.payload;
        if (forEveryone) {
          const msg = state.messages.find((m) => m._id === messageId);
          if (msg) {
            msg.isDeleted = true;
            msg.content = "This message was deleted";
          }
        } else {
          state.messages = state.messages.filter((m) => m._id !== messageId);
        }
      });

    // Archive conversation
    builder
      .addCase(archiveConversation.fulfilled, (state, action) => {
        const conv = state.conversations.find((c) => c._id === action.payload);
        if (conv) {
          conv.isArchived = true;
        }
      });

    // Delete conversation
    builder
      .addCase(deleteConversation.fulfilled, (state, action) => {
        state.conversations = state.conversations.filter((c) => c._id !== action.payload);
        if (state.activeConversationId === action.payload) {
          state.activeConversationId = null;
          state.activeConversation = null;
          state.messages = [];
        }
      });
  },
});

export const {
  setActiveConversation,
  clearActiveConversation,
  addMessage,
  addOptimisticMessage,
  updateMessageStatus,
  addConversation,
  setTypingIndicator,
  setUserOnline,
  setUserOffline,
  incrementUnreadCount,
  clearConversationUnread,
  markMessagesReadLocally,
  removeMessage,
  openChatModal,
  closeChatModal,
  openNewChatModal,
  closeNewChatModal,
  resetChatState,
  clearConversationsError,
  clearMessagesError,
} = chatSlice.actions;

export default chatSlice.reducer;
